<template>
    <div class="flex relative border border-r-0 border-gray-400 mb-8 w-full">
        <div class="flex-1">
            <a :href="detail_route"
                class="flex flex-col sm:flex-row px-4 py-2 group bg-gray-warm-100 hover:bg-blue-100 cursor-pointer items-center justify-between focus:outline-none focus:shadow-none select-none"
                :class="{ 'border-b border-gray-400': show_steps }"
            >
                <div class="w-full sm:w-auto">
                    <div class="flex flex-col sm:flex-row items-baseline justify-center sm:justify-start font-semibold text-black group-hover:text-blue-700 leading-none mt-3 mb-2 sm:my-2"
                        :class="size == 'small' ? 'text-md' : 'text-lg'"
                    >
                        {{ property.address }}{{ city }}{{ state }}

                        <div v-if="property.address && property.client.name"
                            class="hidden sm:block text-base text-gray-warm-700 mx-3 mb-px"
                        >
                            &bull;
                        </div>

                        <div class="font-normal text-gray-warm-700 py-2 sm:py-0"
                            :class="size == 'small' ? 'text-base' : 'text-md'"
                        >
                            {{ property.client.name }}
                        </div>
                    </div>

                    <div class="flex-1 flex items-center justify-between">
                        <archive-property
                            v-if="archivable && ! read_only"
                            :id="property.id"
                            :confirm_archive="confirm_archive"
                            @archived="$emit('archived')"
                        />

                        <delete-property
                            v-if="deletable && ! read_only"
                            :id="property.id"
                            @restored="$emit('restored')"
                            @deleted="$emit('deleted')"
                        />

                        <div></div>

                        <div class="block sm:hidden">
                            <dashboard-property-status
                                :status="property.status.overall"
                            />
                        </div>
                    </div>
                </div>

                <div class="hidden sm:block">
                    <dashboard-property-status
                        :status="property.status.overall"
                    />
                </div>
            </a>

            <div v-if="show_steps">
                <div v-if="size == 'small'"
                    class="flex flex-col sm:flex-row pl-10"
                >
                    <dashboard-step-small
                        label="Inspection"
                        :status="property.status.sections.inspection"
                        :first="true"
                        :order="1"
                        :id="property.id"
                        focus_section="contract-information"
                        focus_field="inspection"
                        :route="route"
                    />

                    <dashboard-step-small
                        label="Attorney Review"
                        :status="property.status.sections.attorney"
                        :order="2"
                        :id="property.id"
                        focus_section="contract-information"
                        focus_field="attorney-review"
                        :route="route"
                    />

                    <dashboard-step-small
                        v-if="! property.mortgage.is_cash_purchase"
                        label="Mortgage Contingency"
                        :status="property.status.sections.mortgage"
                        :order="3"
                        :id="property.id"
                        focus_section="contract-information"
                        focus_field="mortgage-contingency"
                        :route="route"
                    />

                    <dashboard-step-small
                        label="Closing"
                        :status="property.status.sections.closing"
                        :order="4"
                        :id="property.id"
                        focus_section="contract-information"
                        focus_field="scheduled-closing"
                        :route="route"
                    />

                    <dashboard-step-small
                        label="Listing Expiration"
                        :status="property.status.sections['listing-detail']"
                        :last="true"
                        :order="5"
                        :id="property.id"
                        focus_section="listing-information"
                        focus_field="list-date"
                        :route="route"
                    />
                </div>

                <div v-else
                    class="flex"
                >
                    <dashboard-step
                        label="Inspection Date"
                        :status="property.status.sections.inspection"
                        :date="property.inspection.inspection_date"
                        :first="true"
                        :order="1"
                        :id="property.id"
                        focus_section="contract-information"
                        focus_field="inspection"
                        :route="route"
                    />

                    <dashboard-step
                        label="Attorney Review Date"
                        :status="property.status.sections.attorney"
                        :date="property.attorney.attorney_review_date"
                        :order="2"
                        :id="property.id"
                        focus_section="contract-information"
                        focus_field="attorney-review"
                        :route="route"
                    />

                    <dashboard-step
                        v-if="! property.mortgage.is_cash_purchase"
                        label="Mortgage Contingency Date"
                        :status="property.status.sections.mortgage"
                        :date="property.mortgage.mortgage_contingency_due_date"
                        :order="3"
                        :id="property.id"
                        focus_section="contract-information"
                        focus_field="mortgage-contingency"
                        :route="route"
                    />

                    <dashboard-step
                        label="Closing Date"
                        :status="property.status.sections.closing"
                        :date="property.closing.scheduled_closing_date"
                        :order="4"
                        :id="property.id"
                        focus_section="contract-information"
                        focus_field="scheduled-closing"
                        :route="route"
                    />

                    <dashboard-step
                        label="Listing Expiration Date"
                        :status="property.status.sections['listing-detail']"
                        :date="property.deal.listing_detail.expire_date"
                        :order="5"
                        :id="property.id"
                        focus_section="listing-information"
                        focus_field="list-date"
                        :route="route"
                    />

                    <dashboard-view-details
                        :property_id="property.id"
                        :closing_date="property.closing_date_text"
                        :route="detail_route"
                    />
                </div>
            </div>

            <dashboard-view-details-small
                v-if="show_detail_link && size == 'small'"
                :property_id="property.id"
                :closing_date="property.closing_date_text"
                :route="detail_route"
            />
        </div>

        <div class="border-r-4" :class="status_class"></div>
    </div>
</template>

<script>
import ArchiveProperty from './archive-property.vue';
import DeleteProperty from './delete-property.vue';
import DashboardPropertyStatus from './dashboard-property-status.vue';
import DashboardStep from './dashboard-step.vue';
import DashboardStepSmall from './dashboard-step-small.vue';
import DashboardViewDetails from './dashboard-view-details.vue';
import DashboardViewDetailsSmall from './dashboard-view-details-small.vue';

export default {
    components: {
        ArchiveProperty,
        DeleteProperty,
        DashboardPropertyStatus,
        DashboardStep,
        DashboardStepSmall,
        DashboardViewDetails,
        DashboardViewDetailsSmall,
    },

    props: {
        property: {},
        route: {},
        detail_route: {},
        read_only: { default: false },
        size: { default: 'normal' },
        show_steps: { default: true },
        show_detail_link: { default: true },
        archivable: { default: true },
        confirm_archive: { default: true },
        deletable: { default: false },
    },

    computed: {
        city() {
            return this.property.city ? ', ' + this.property.city : '';
        },
        state() {
            return this.property.state ? ', ' + this.property.state : '';
        },
        status_class() {
            switch (this.property.status.overall) {
                case 'red':
                    return 'border-red-500';
                case 'yellow':
                    return 'border-yellow-500';
                default:
                    return 'border-green-500';
            }
        },
    },
}
</script>
